/**
 * Static collision for the client-local dungeon interior.
 *
 * Why this exists (`q-dungeon-playable-not-preview`, half (A)): `DungeonExplorer`
 * shipped as an O-025 preview with NO wall collision at all — its own comment said
 * so — and clamped the walker to the interior AABB, so every wall was passable.
 *
 * Two rules this module keeps:
 *
 * - **The collider is derived from the LAYOUT, never from the render meshes**
 *   (WR.2 / AD-044: the shared collider is the source of truth). It reads exactly
 *   the same plan, spacing and `DUNGEON_INTERIOR_SCALE` numbers that `interior.ts`
 *   assembles geometry from, so a wall and its collider cannot drift apart without
 *   one of those inputs changing.
 * - **No `three` import.** The output is `CollisionShape[]` in the shared 2.5D
 *   contract (flat x/z), in the interior group's LOCAL frame — the same frame the
 *   assembled meshes use — so this file is a pure function and is unit-tested in
 *   Node without a WebGL context.
 *
 * Scope, stated so it is not mistaken for more than it is: this covers the walls
 * the assembler builds — room sides (solid, doorway-split, or secret-entrance),
 * corridor runs, and the secret alcove. Dressing props, enemy proxies and the
 * server-authoritative world are deliberately NOT here; the interior is still a
 * client-local space and nothing in this module goes on the wire.
 */

import { placedBox, secretAlcoveShapes, type CollisionShape } from '@great-cloud/shared/client';
import type { DungeonPlan, DungeonRoom } from '@great-cloud/procedural-dungeons';

import { DUNGEON_INTERIOR_SCALE, DUNGEON_SECRET_AREA_SCALE } from './interiorScale.js';

const SCALE = DUNGEON_INTERIOR_SCALE;
const SECRET = DUNGEON_SECRET_AREA_SCALE;
const ROOM_SIZE = SCALE.roomSize;
const HALF_ROOM = ROOM_SIZE / 2;
const HALF_WALL = SCALE.wallThickness / 2;

/** A cardinal side of a room, in the assembler's own `{dx, dz, yaw}` form. */
export interface InteriorSide {
  dx: number;
  dz: number;
  yaw: number;
}

export const INTERIOR_CARDINAL_SIDES: readonly InteriorSide[] = [
  { dx: 1, dz: 0, yaw: -Math.PI / 2 },
  { dx: -1, dz: 0, yaw: Math.PI / 2 },
  { dx: 0, dz: 1, yaw: Math.PI },
  { dx: 0, dz: -1, yaw: 0 },
];

/**
 * What the secret area contributes to collision. `createSecretArea` picks the host
 * room, the side and the panel side by hash, so those facts are passed in rather
 * than re-derived here — re-deriving them would be a second implementation of the
 * same hash choice, and the two could disagree.
 */
export interface SecretCollisionFacts {
  hostRoomId: string;
  side: InteriorSide;
  /** Which half of the entrance the false-wall panel covers; the other half is the concealed gap. */
  panelOnNegativeSide: boolean;
}

/** The clear opening a doorway leaves, measured between its jambs. */
export const DOORWAY_CLEAR_HALF_WIDTH = SCALE.doorwayClearWidth / 2;

/** The clear opening the secret false wall leaves once the panel is accounted for. */
export const SECRET_ENTRANCE_CLEAR_HALF_WIDTH = SECRET.secretEntranceClearWidth / 2;

/**
 * Exactly what the derivation reads from a plan, named so the dependency is stated
 * rather than implied by a whole-plan parameter — and so a test can build a layout
 * without fabricating generator metadata it does not exercise.
 */
export type InteriorLayoutPlan = Pick<DungeonPlan, 'rooms' | 'connections'>;

function roomKey(room: DungeonRoom): string {
  return `${room.coordinate.x},${room.coordinate.z}`;
}

/**
 * Derive the static collision set for an assembled interior — the **v1 fallback**
 * (a schema-1 plan with no `layout`). Kept for exactly one release per AD-404 §8;
 * a v2 plan goes through `buildLayoutCollision`.
 *
 * `spacing` MUST be the same value `buildDungeonInterior` used (it expands the
 * authored room spacing to guarantee a corridor run), and `secret` the facts
 * `createSecretArea` chose — pass `null` when the plan has no secret.
 */
export function buildInteriorCollision(
  plan: InteriorLayoutPlan,
  spacing: number,
  secret: SecretCollisionFacts | null,
): CollisionShape[] {
  const shapes: CollisionShape[] = [];

  const connected = new Set<string>();
  for (const connection of plan.connections) {
    connected.add(`${connection.a}->${connection.b}`);
    connected.add(`${connection.b}->${connection.a}`);
  }
  const roomByCoordinate = new Map(plan.rooms.map((room) => [roomKey(room), room]));
  const corridorsAdded = new Set<string>();

  for (const room of plan.rooms) {
    const centreX = room.coordinate.x * spacing;
    const centreZ = room.coordinate.z * spacing;

    for (const side of INTERIOR_CARDINAL_SIDES) {
      const neighbour = roomByCoordinate.get(
        `${room.coordinate.x + side.dx},${room.coordinate.z + side.dz}`,
      );
      const isOpen = neighbour !== undefined && connected.has(`${room.id}->${neighbour.id}`);
      const wallX = centreX + side.dx * HALF_ROOM;
      const wallZ = centreZ + side.dz * HALF_ROOM;
      const isSecretEntrance =
        secret !== null &&
        secret.hostRoomId === room.id &&
        secret.side.dx === side.dx &&
        secret.side.dz === side.dz;

      if (isOpen && neighbour) {
        // Solid either side of the doorway's clear opening; the jambs are folded
        // into the wall spans, so what is left passable is exactly the opening.
        for (const sign of [-1, 1] as const) {
          const inner = sign * DOORWAY_CLEAR_HALF_WIDTH;
          const outer = sign * HALF_ROOM;
          shapes.push(
            placedBox(
              wallX,
              wallZ,
              side.yaw,
              Math.min(inner, outer),
              Math.max(inner, outer),
              -HALF_WALL,
              HALF_WALL,
            ),
          );
        }

        const corridorKey = [room.id, neighbour.id].sort().join('<->');
        if (!corridorsAdded.has(corridorKey)) {
          corridorsAdded.add(corridorKey);
          const corridorLength = spacing - ROOM_SIZE;
          const corridorX = centreX + side.dx * (spacing / 2);
          const corridorZ = centreZ + side.dz * (spacing / 2);
          for (const sign of [-1, 1] as const) {
            const near = sign * DOORWAY_CLEAR_HALF_WIDTH;
            const far = sign * (DOORWAY_CLEAR_HALF_WIDTH + SCALE.wallThickness);
            shapes.push(
              placedBox(
                corridorX,
                corridorZ,
                side.yaw,
                Math.min(near, far),
                Math.max(near, far),
                -corridorLength / 2,
                corridorLength / 2,
              ),
            );
          }
        }
      } else if (isSecretEntrance && secret) {
        // The room wall is tiled to either side of the secret entrance span, and
        // the false-wall panel covers one half of that span. The other half is the
        // concealed gap — deliberately PASSABLE, because that gap is the secret.
        for (const sign of [-1, 1] as const) {
          const inner = sign * SECRET_ENTRANCE_CLEAR_HALF_WIDTH;
          const outer = sign * HALF_ROOM;
          shapes.push(
            placedBox(
              wallX,
              wallZ,
              side.yaw,
              Math.min(inner, outer),
              Math.max(inner, outer),
              -HALF_WALL,
              HALF_WALL,
            ),
          );
        }
        const panelCentreX = secret.panelOnNegativeSide
          ? -SECRET.secretPanelGapWidth / 2
          : SECRET.secretPanelGapWidth / 2;
        shapes.push(
          placedBox(
            wallX,
            wallZ,
            side.yaw,
            panelCentreX - SECRET.secretPanelWidth / 2,
            panelCentreX + SECRET.secretPanelWidth / 2,
            -HALF_WALL,
            HALF_WALL,
          ),
        );
        shapes.push(
          ...secretAlcoveShapes(
            wallX,
            wallZ,
            side.yaw,
            SECRET.secretAlcoveWidth,
            SECRET.secretAlcoveDepth,
          ),
        );
      } else {
        shapes.push(
          placedBox(wallX, wallZ, side.yaw, -HALF_ROOM, HALF_ROOM, -HALF_WALL, HALF_WALL),
        );
      }
    }
  }

  return shapes;
}

/**
 * The schema-v2 collider (every wall span of the layout's wall plan, the secret's
 * false-wall panel and its alcove) lives in `@great-cloud/shared`
 * (`sim/dungeon-walls.ts`, AD-698): the server's dungeon instance zone collides
 * against the same shapes, so it is one walk for prediction and authority alike.
 */
export { buildLayoutCollision, wallSpanFootprint } from '@great-cloud/shared/client';
